// components/HealthStatusPage.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useHealth } from "../contexts/HealthContext";

// Health threshold constants
const API_ERROR_THRESHOLD = 3.0; // More than 5% API errors is problematic
const PROCESSING_THRESHOLD = 70.0; // Less than 80% processed is problematic

const HealthStatusIcon = () => {
    const { summary, loadingSummary } = useHealth();
    const [showTooltip, setShowTooltip] = useState(false);

    // Calculate health status
    let isOk = false;
    let isWarning = false;

    if (summary) {
        // Calculate key metrics
        const processedPercentage1h = summary.recs_new_1h > 0
            ? (100 * summary.recs_ready_1h / summary.recs_new_1h)
            : 0;

        const processedPercentage24h = summary.recs_new_24h > 0
            ? (100 * summary.recs_ready_24h / summary.recs_new_24h)
            : 0;

        const apiErrorPercentage1h = summary.api_called_1h > 0
            ? (100 * summary.api_errors_1h / summary.api_called_1h)
            : 0;

        const apiErrorPercentage24h = summary.api_called_24h > 0
            ? (100 * summary.api_errors_24h / summary.api_called_24h)
            : 0;

        // Check conditions for OK status (1h metrics)
        const hasRecentCalls = summary.recs_ready_1h > 0;
        const goodProcessingRate1h = processedPercentage1h >= PROCESSING_THRESHOLD;
        const lowErrorRate1h = apiErrorPercentage1h <= API_ERROR_THRESHOLD;
        const fixedTaxonomyOk = !window.appSettings.fixedTaxonomyEnabled ||
            summary.recs_fixedintent_1h > 0;

        isOk = hasRecentCalls && goodProcessingRate1h && lowErrorRate1h && fixedTaxonomyOk;

        // If not OK, check for warning status (24h metrics)
        if (!isOk) {
            const hasRecentCallsLonger = summary.recs_ready_24h > 0;
            const goodProcessingRate24h = processedPercentage24h >= PROCESSING_THRESHOLD;
            const lowErrorRate24h = apiErrorPercentage24h <= API_ERROR_THRESHOLD;
            const fixedTaxonomyWarning = !window.appSettings.fixedTaxonomyEnabled ||
                summary.recs_fixedintent_24h > 0;

            isWarning = hasRecentCallsLonger && goodProcessingRate24h &&
                lowErrorRate24h && fixedTaxonomyWarning;
        }
    }

    // Create human-readable tooltip content
    const getTooltipContent = () => {
        if (loadingSummary) {
            return "Loading health status...";
        }

        if (!summary) {
            return "No health data available";
        }

        let status = isOk ? "Healthy" : isWarning ? "Warning" : "Needs Attention";
        const problems = [];

        // Calculate percentages for display
        const processedPercentage1h = summary.recs_new_1h > 0
            ? (100 * summary.recs_ready_1h / summary.recs_new_1h).toFixed(1)
            : "0.0";

        const processedPercentage24h = summary.recs_new_24h > 0
            ? (100 * summary.recs_ready_24h / summary.recs_new_24h).toFixed(1)
            : "0.0";

        const apiErrorPercentage1h = summary.api_called_1h > 0
            ? (100 * summary.api_errors_1h / summary.api_called_1h).toFixed(1)
            : "0.0";

        const apiErrorPercentage24h = summary.api_called_24h > 0
            ? (100 * summary.api_errors_24h / summary.api_called_24h).toFixed(1)
            : "0.0";

        // Identify problems
        if (summary.recs_ready_1h === 0) {
            problems.push("No processed calls in the last hour");
        }

        if (parseFloat(processedPercentage1h) < PROCESSING_THRESHOLD) {
            problems.push(`Low processing rate in last hour: ${processedPercentage1h}% (threshold: ${PROCESSING_THRESHOLD}%)`);
        }

        if (parseFloat(apiErrorPercentage1h) > API_ERROR_THRESHOLD) {
            problems.push(`High API error rate in last hour: ${apiErrorPercentage1h}% (threshold: ${API_ERROR_THRESHOLD}%)`);
        }

        if (parseFloat(apiErrorPercentage24h) > API_ERROR_THRESHOLD) {
            problems.push(`High API error rate in last 24h: ${apiErrorPercentage24h}% (threshold: ${API_ERROR_THRESHOLD}%)`);
        }

        if (window.appSettings.fixedTaxonomyEnabled && summary.recs_fixedintent_1h === 0) {
            problems.push("No fixed intent calls in the last hour");
        }

        if (summary.sync_errors_1h > 0) {
            problems.push(`${summary.sync_errors_1h} sync errors in the last hour`);
        }

        if (summary.combine_errors_1h > 0) {
            problems.push(`${summary.combine_errors_1h} combine errors in the last hour`);
        }

        // Format the tooltip content
        let content = `Status: ${status}\n\n`;

        if (problems.length > 0) {
            content += "Issues:\n";
            problems.forEach(problem => {
                content += `* ${problem}\n`;
            });
            content += "\n";
        }

        content += "Stats (last hour):\n";
        content += `* New calls processed: ${summary.recs_ready_1h}/${summary.recs_new_1h} (${processedPercentage1h}%)\n`;
        content += `* API error rate: ${apiErrorPercentage1h}% (${summary.api_errors_1h}/${summary.api_called_1h})\n`;

        if (window.appSettings.fixedTaxonomyEnabled) {
            content += `* Fixed intent calls: ${summary.recs_fixedintent_1h}\n`;
        }

        content += "\nStats (last 24 hours):\n";
        content += `* New calls processed: ${summary.recs_ready_24h}/${summary.recs_new_24h} (${processedPercentage24h}%)\n`;
        content += `* API error rate: ${apiErrorPercentage24h}% (${summary.api_errors_24h}/${summary.api_called_24h})\n`;

        if (window.appSettings.fixedTaxonomyEnabled) {
            content += `* Fixed intent calls: ${summary.recs_fixedintent_24h}\n`;
        }

        return content;
    };

    return (
        <Link to="/health-status" className="ml-4 relative">
            <div
                className="w-6 h-6 flex items-center justify-center rounded-full cursor-pointer"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                {loadingSummary ? (
                    // Loading spinner
                    <svg className="animate-spin h-5 w-5 text-stone-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                ) : isOk ? (
                    // Green checkmark status
                    <div className="usan-green w-full h-full rounded-full flex items-center justify-center">
                        <svg className="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </div>
                ) : isWarning ? (
                    // Yellow warning status
                    <div className="bg-yellow-500 w-full h-full rounded-full flex items-center justify-center">
                        <svg className="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                ) : (
                    // Red error status
                    <div className="bg-red-600 w-full h-full rounded-full flex items-center justify-center">
                        <svg className="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                )}

                {/* Custom Tooltip */}
                {showTooltip && (
                    <div className="absolute z-50 w-80 p-3 -left-36 top-8 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-pre-wrap">
                        {getTooltipContent()}
                    </div>
                )}
            </div>
        </Link>
    );
};

export default HealthStatusIcon;